import Swal from "sweetalert2";
import PropTypes from 'prop-types';
import { Col } from 'react-bootstrap';
import styled from 'styled-components';
import Badge from '@/shared/components/Badge';
import { colorGreenHover } from '@/utils/palette';
import React, { useEffect, useState } from 'react';
import { Typography, Tooltip } from "@mui/material";
import AdminInterface from '@/utils/AdminInterface';
import DownloadBoxIcon from 'mdi-react/DownloadBoxIcon';
import withReactContent from "sweetalert2-react-content";
import ProgressCloseIcon from 'mdi-react/ProgressCloseIcon';
import ProgressCheckIcon from 'mdi-react/ProgressCheckIcon';
import ProgressClockIcon from 'mdi-react/ProgressClockIcon';
import { paddingLeft, paddingRight } from '@/utils/directions';
import ProgressSpannerIcon from 'mdi-react/ProgressSpannerIcon';
import { colorAdditional, colorRedHover } from '@/utils/palette';
import ReactTableBase from '@/shared/components/table/ReactTableBase';
import InformationCircleOutlineIcon from 'mdi-react/InformationCircleOutlineIcon';
import { Card, CardBody, CardTitleWrap, CardTitle } from '@/shared/components/Card';
import { BatchJobStorageEnum, BatchJobVCPUsEnum, JobStatusEnum } from '../../../../../Enums';

const CartTableButton = styled.button`
  background: transparent;
  border: none;
  line-height: 14px;
  ${paddingLeft}: 20px;
  ${paddingRight}: 0;
  padding-top: 0;
  padding-bottom: 0;
  position: relative;
  color: ${colorAdditional};
  cursor: pointer;
  transition: all 0.3s;

  svg {
    height: 20px;
    width: 20px;
    fill: ${colorAdditional};
    transition: all 0.3s;
  }

  &:hover {
    color: ${colorRedHover};

    svg {
      fill: ${colorRedHover};
    }
  }

  &:hover .edit {
    color: ${colorGreenHover};

    svg {
      fill: ${colorGreenHover};
    }
  }
`;

const StatusFormatter = value => (
    value ? <Badge bg="success">True</Badge> : <Badge bg="secondary">False</Badge>
);

StatusFormatter.propTypes = {
    value: PropTypes.bool.isRequired,
};

const CropRequestsList = () => {
    const [cropRequestsList, setCropRequestsList] = useState({ tableHeaderData: [], tableRowsData: [] });
    const tableConfig = {
        isEditable: false,
        isSortable: true,
        isResizable: false,
        withPagination: true,
        withSearchEngine: true,
        manualPageSize: [10, 20, 30, 40],
        placeholder: 'Search...',
    };

    const GetCropRequestsData = () => {
        AdminInterface.instance.getCropRequests().then((response) => {
            const columns = [
                {
                    accessor: 'id',
                    Header: 'Id',
                    disableGlobalFilter: true,
                    disableSortBy: true,
                },
                {
                    accessor: 'collection',
                    Header: 'Collection',
                },
                {
                    accessor: 'mission',
                    Header: 'Mission',
                },
                {
                    accessor: 'area',
                    Header: 'Area (km²)',
                    disableGlobalFilter: true,
                },
                {
                    accessor: 'price',
                    Header: 'Price',
                    disableGlobalFilter: true,
                },
                {
                    accessor: 'start_datetime',
                    Header: 'Start Datetime',
                    disableGlobalFilter: true

                },
                {
                    accessor: 'status_datetime',
                    Header: 'Status Datetime',
                    disableGlobalFilter: true
                },
                {
                    accessor: 'info',
                    Header: 'Info',
                    disableGlobalFilter: true,
                    disableSortBy: true,
                },
                {
                    accessor: 'status',
                    Header: 'Status',
                    disableGlobalFilter: true
                },
                {
                    accessor: 'isPaid',
                    Header: 'Is Paid?',
                    disableGlobalFilter: true,
                },
                {
                    accessor: 'outputAddress',
                    Header: 'Output',
                    disableGlobalFilter: true,
                    disableSortBy: true,
                }
            ];
            const data = [];
            response.data.forEach(request => {
                data.push({
                    id: request.id,
                    collection: request.mission.collection.name,
                    mission: request.mission.name,
                    area: `${request.area}`,
                    price: `CAD $${request.price/100}`,
                    start_datetime: new Date(`${request.start_datetime}z`).toLocaleString(),
                    status_datetime: request.status_datetime !== null ? new Date(`${request.status_datetime}z`).toLocaleString() : '',
                    info: <Tooltip title={
                        <Typography fontSize={12}>
                            Job ID: {request.job_id}<br/>
                            User Requested: {request.user_requested}<br/>
                            VCPUs: {Object.keys(BatchJobVCPUsEnum).find(key => BatchJobVCPUsEnum[key] === request.batch_job_vcpus)}<br/>
                            Storage: {Object.keys(BatchJobStorageEnum).find(key => BatchJobStorageEnum[key] === request.batch_job_storage)}<br/>
                            Resolution: {request.resolution}<br/>
                            Output Format: {request.outputFormat}<br/>
                            Output CRS: {request.outputCRS}<br/>
                        </Typography>
                    } placement='bottom-start'><span><InformationCircleOutlineIcon color='#ddd' /></span></Tooltip>,
                    isPaid: StatusFormatter(request.isPaid),
                    outputAddress: <CartTableButton type="button" onClick={() => window.open(request.outputAddress)}><DownloadBoxIcon /></CartTableButton>,
                    status: request.status === JobStatusEnum.completed ? <Tooltip title={<Typography fontSize={12}>Completed</Typography>} placement='bottom-start'><span><ProgressCheckIcon color='#b8e986' /></span></Tooltip> :
                            request.status === JobStatusEnum.failed ? <Tooltip title={<Typography fontSize={12}>Failed</Typography>} placement='bottom-start'><span><ProgressCloseIcon color='#f88e7d' /></span></Tooltip> :
                            request.status === JobStatusEnum.running ? <Tooltip title={<Typography fontSize={12}>Running</Typography>} placement='bottom-start'><span><ProgressClockIcon color='#f6da6e' /></span></Tooltip> :
                            <Tooltip title={<Typography fontSize={12}>Not Started</Typography>} placement='bottom-start'><span><ProgressSpannerIcon color='#ddd' /></span></Tooltip>,
                });
            });

            const cropRequestsListTableData = { tableHeaderData: columns, tableRowsData: data };
            setCropRequestsList(cropRequestsListTableData);
        }).catch((error) => {
            withReactContent(Swal).fire({
                title: "Error",
                html: error.response.data?.detail,
            });
        })
    };

    useEffect(() => {
        GetCropRequestsData();
    }, []);

    return (
        <Col md={12} lg={12}>
            <Card>
                <CardBody>
                    <CardTitleWrap>
                        <CardTitle>Crop Requests list</CardTitle>
                    </CardTitleWrap>
                    <ReactTableBase
                        columns={cropRequestsList.tableHeaderData}
                        data={cropRequestsList.tableRowsData}
                        tableConfig={tableConfig}
                    />
                </CardBody>
            </Card>
        </Col>
    );
};

CropRequestsList.propTypes = {
    cropRequestsListTableData: PropTypes.shape({
        tableHeaderData: PropTypes.arrayOf(PropTypes.shape({
            key: PropTypes.string,
            name: PropTypes.string,
        })),
        tableRowsData: PropTypes.arrayOf(PropTypes.shape()),
    }).isRequired,
};

export default CropRequestsList;
